// PpdsLabel — read-only PPDS (Natasha's Law) label surface.
//
// Endpoints used (both GET, org-scoped, no writes):
//   GET /api/costing/recipes                  — read probe + recipe picker
//   GET /api/costing/recipes/{id}/ppds-label  — assembled label payload
//
// There is no /api/costing/capabilities endpoint, so the read probe IS the
// capability gate: a non-array body, a 401/403/404, or a transport failure
// blocks the whole surface and nothing is rendered — the same fail-closed rule
// Controls/Workforce/Marketplace apply to their explicit /capabilities call.
//
// READ-ONLY BY CONSTRUCTION. This file issues GET requests only and never
// asserts an allergen-free or safe-to-eat claim about a food. The payload's own
// NOTICE and LIMITATIONS are rendered verbatim; the wording added here only
// ever disclaims.
//
// PRINTING IS GATED (see `printBlockers`). A label can be sent to the browser's
// print dialog only when the payload status is exactly `reviewed`, it lists no
// unverified inputs, the name of the food and a date marking are present, and
// an operator has ticked the confirmation box. Anything else shows why printing
// is unavailable instead of a button. Printing uses window.print with a print
// stylesheet that isolates the compact label; nothing is sent to a device or
// written anywhere by this code.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase } from '../utils/authHeaders';
import {
  errorMessage, readMktScope, requestConfig, scopeKey, useMktScope,
} from './MarketplaceShared';
import './Workforce.css';
import './PpdsLabel.css';

export const ppdsApi = axios.create({ baseURL: `${apiBase()}/api/costing` });

//: Display names for the UK14 codes the payload carries per entry. Mirrors the
//: backend's UK14_DISPLAY; an unrecognised code falls back to the raw code so an
//: allergen is never silently dropped from the screen.
const UK14_DISPLAY = {
  celery: 'celery',
  cereals_containing_gluten: 'cereals containing gluten',
  crustaceans: 'crustaceans',
  eggs: 'egg',
  fish: 'fish',
  lupin: 'lupin',
  milk: 'milk',
  molluscs: 'molluscs',
  mustard: 'mustard',
  nuts: 'nuts',
  peanuts: 'peanuts',
  sesame: 'sesame',
  soybeans: 'soya',
  sulphur_dioxide_and_sulphites: 'sulphur dioxide and sulphites',
};
export const allergenName = code => UK14_DISPLAY[code] || code;

/**
 * Reasons a loaded label must not be printed. Empty array = printable once an
 * operator confirms. Every condition fails closed on a missing or odd field.
 */
export function printBlockers(label) {
  if (!label || typeof label !== 'object') return ['No label is loaded.'];
  const reasons = [];
  // SAF02d: live compliance claims are blocked until a human has reviewed the
  // requirements for the jurisdiction this site operates in. Printing is a live
  // claim, so the gate blocks it.
  if (label.live_claims_blocked === true) {
    reasons.push(`Jurisdiction review outstanding: ${label.jurisdiction?.reason || 'the site jurisdiction has not been reviewed'}`);
  }
  if (label.status !== 'reviewed') reasons.push('Allergen status is not reviewed.');
  if (!Array.isArray(label.issues) || label.issues.length > 0) reasons.push('The label lists unverified inputs.');
  if (!(typeof label.name_of_food === 'string' && label.name_of_food.trim())) reasons.push('The name of the food is not recorded.');
  if (!(label.date_marking?.present === true && label.date_marking.value)) reasons.push('No date marking is recorded.');
  if (!(typeof label.ingredients_text === 'string' && label.ingredients_text.trim())) reasons.push('No ingredients list was assembled.');
  if (!Array.isArray(label.ingredients) || label.ingredients.some(row => row?.allergen_status !== 'reviewed')) {
    reasons.push('At least one ingredient has an unverified allergen status.');
  }
  return reasons;
}

const DATE_WORDS = { use_by: 'Use by', best_before: 'Best before' };

export const PPDS_BLOCKED =
  'PPDS label data is unavailable for this organization, or you do not have access.';

/**
 * Render a payload string with its inline `**emphasis**` markers turned into
 * real bold. An odd number of markers leaves the trailing segment emphasised
 * rather than dropped — the list must never lose allergen text to a marker bug.
 */
export function EmphasisedText({ text }) {
  return String(text ?? '')
    .split('**')
    .map((part, index) => (index % 2
      ? <strong key={index} data-testid="ppds-emphasis">{part}</strong>
      : <React.Fragment key={index}>{part}</React.Fragment>));
}

// Nav gate: the nav item is shown only while the caller can actually read the
// costing recipes collection. Anything but an array — including a rejection —
// leaves it hidden.
export function usePpdsNavEnabled(token) {
  const scope = useMktScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (scope.orgId && scope.token) {
      ppdsApi.get('/recipes', requestConfig(scope, controller.signal))
        .then(res => { if (!controller.signal.aborted && Array.isArray(res?.data)) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope]);
  return allowedKey === key;
}

export default function PpdsLabel() {
  const scope = useMktScope();
  return <PpdsLabelWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function PpdsLabelWorkspace({ scope }) {
  const lifetime = useRef(new AbortController());
  const revision = useRef(0);
  const [caps, setCaps] = useState(null);
  const [blocked, setBlocked] = useState('');
  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [recipeId, setRecipeId] = useState('');
  const [label, setLabel] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { const c = new AbortController(); lifetime.current = c; return () => c.abort(); }, []);
  const active = useCallback(
    () => !lifetime.current.signal.aborted && scopeKey(readMktScope()) === scopeKey(scope),
    [scope],
  );
  const config = useCallback(() => requestConfig(scope, lifetime.current.signal), [scope]);

  const loadCaps = useCallback(async () => {
    const rev = ++revision.current;
    setLoading(true); setCaps(null); setBlocked(''); setError(''); setLabel(null); setRecipes([]);
    try {
      if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to view a PPDS label.');
      const rows = (await ppdsApi.get('/recipes', config())).data;
      if (!active() || rev !== revision.current) return;
      if (!Array.isArray(rows)) throw new Error(PPDS_BLOCKED);
      setRecipes(rows.filter(row => row && typeof row.id === 'string' && row.id));
      setCaps({ can_read: true });
    } catch (e) {
      if (rev === revision.current) { setCaps(null); setBlocked(errorMessage(e)); }
    } finally {
      if (active() && rev === revision.current) setLoading(false);
    }
  }, [active, config, scope]);
  useEffect(() => { loadCaps(); }, [loadCaps]);

  const load = async () => {
    const id = recipeId.trim();
    if (!caps?.can_read || !id || busy) return;
    const rev = ++revision.current;
    // Clear the previous label BEFORE the request: a failed load must leave the
    // screen with an error and no label, never a stale one.
    setBusy(true); setError(''); setLabel(null); setConfirmed(false);
    try {
      const data = (await ppdsApi.get(`/recipes/${encodeURIComponent(id)}/ppds-label`, config())).data;
      if (!active() || rev !== revision.current) return;
      if (!data || typeof data !== 'object' || !Array.isArray(data.ingredients)) {
        throw new Error('Unexpected PPDS label response; nothing is shown rather than an unverified label.');
      }
      setLabel(data);
    } catch (e) {
      if (active() && rev === revision.current) setError(errorMessage(e));
    } finally {
      if (active() && rev === revision.current) setBusy(false);
    }
  };

  if (loading) return <div className="wf-ops" data-testid="ppds-loading">Loading PPDS label data…</div>;
  if (blocked || !caps) {
    return <div className="wf-ops wf-blocked" data-testid="ppds-blocked"><p>{blocked || PPDS_BLOCKED}</p></div>;
  }

  const selected = recipes.some(row => row.id === recipeId) ? recipeId : '';
  return <main className="wf-ops" data-testid="ppds-label-surface">
    <header><h1>PPDS label (Natasha&apos;s Law)</h1><button type="button" disabled={busy} onClick={loadCaps}>Refresh</button></header>
    <p>Read-only label data assembly for prepacked food for direct sale. Nothing here is written to any record or sent anywhere. Printing is only offered for a fully reviewed label after an operator confirms it. This is a draft for an operator to verify and complete — confirm supplier statements, the name of the food, the ingredients list and the date marking before the pack is sold.</p>
    {error && <p role="alert" className="wf-error" data-testid="ppds-error">{error}</p>}
    {busy && <p role="status">Loading label…</p>}
    <form aria-label="Load PPDS label" onSubmit={e => { e.preventDefault(); load(); }}>
      <fieldset disabled={busy}>
        <div className="wf-actions">
          {recipes.length > 0 && <label className="wf-muted">Recipe
            <select aria-label="Recipe" data-testid="ppds-recipe-select" value={selected} onChange={e => { setRecipeId(e.target.value); setLabel(null); setError(''); }}>
              <option value="">Choose a recipe</option>
              {recipes.map(row => <option key={row.id} value={row.id}>{row.name || row.id}</option>)}
            </select>
          </label>}
          <label className="wf-muted">Recipe ID
            <input aria-label="Recipe ID" data-testid="ppds-recipe-id" value={recipeId} onChange={e => { setRecipeId(e.target.value); setLabel(null); setError(''); }} required />
          </label>
          <button type="submit" data-testid="ppds-load" disabled={!recipeId.trim()}>Load label</button>
        </div>
      </fieldset>
    </form>
    {!recipes.length && <p>No recipes are available for this organization. Enter a recipe ID to assemble a label.</p>}

    {label && <section className="ppds-label" data-testid="ppds-label">
      {label.live_claims_blocked === true && (
        <p role="alert" className="wf-error" data-testid="ppds-jurisdiction">
          <strong>Draft only — live compliance claims are blocked for this site.</strong>{' '}
          {label.jurisdiction?.reason
            || 'The compliance requirements for this site\u2019s jurisdiction have not been reviewed.'}{' '}
          This label is assembled data for an operator to inspect, not a claim that may be printed on a pack.
        </p>
      )}
      {label.status === 'reviewed'
        ? <p className="wf-notice" data-testid="ppds-status"><strong>Allergen status: reviewed.</strong> The inputs this label was assembled from were reviewed. That is not a claim about the food, and this is still a draft for a human to check and complete.</p>
        : <p role="alert" className="wf-error" data-testid="ppds-status"><strong>Allergen status: {label.status === 'unknown' ? 'unknown' : `not recognised (${label.status ?? 'missing'})`}.</strong> At least one contributing declaration, ingredient reference or recipe line could not be verified, so this list is not a complete allergen declaration. Do not treat it as verified.</p>}

      <h2 data-testid="ppds-name-of-food">{label.name_of_food || 'Name of the food not recorded on this recipe.'}</h2>
      <p data-testid="ppds-date-marking">{label.date_marking?.present === true && label.date_marking.value
        ? `${label.date_marking.type === 'best_before' ? 'Best before' : label.date_marking.type === 'use_by' ? 'Use by' : 'Date marking'}: ${label.date_marking.value} (copied from the recipe record; not calculated, validated against shelf life, or printed here)`
        : 'No date marking recorded on this recipe.'}</p>

      <h3>Ingredients (descending weight order)</h3>
      <p>Descending weight order where quantities are recorded; entries without a recorded quantity are listed last, then by name. {label.ingredient_count ?? 0} ingredient(s).</p>
      <ol data-testid="ppds-ingredients">
        {(label.ingredients || []).map((row, index) => <li key={row.ref_id || index} data-testid="ppds-ingredient">
          <span data-testid="ppds-ingredient-name">{row.name || `[unresolved ingredient ${row.ref_id}]`}</span>
          {' — '}{row.weight_known === true && row.weight !== null && row.weight !== undefined
            ? `${row.weight}${row.unit ? ` ${row.unit}` : ''}`
            : 'quantity not recorded'}
          {(row.allergens || []).length > 0 && <> (<strong data-testid="ppds-allergen">{row.allergens.map(allergenName).join(', ')}</strong>)</>}
          {row.allergen_status === 'unknown' && <em> [allergen status unverified]</em>}
        </li>)}
      </ol>
      {!(label.ingredients || []).length && <p>No ingredients were resolved for this recipe. That is not evidence that the recipe has no ingredients.</p>}

      <h3>Assembled ingredients list</h3>
      <p data-testid="ppds-ingredients-text"><EmphasisedText text={label.ingredients_text} /></p>
      <p data-testid="ppds-contains">Allergens declared against the listed ingredients: {label.contains?.length ? label.contains.map(allergenName).join(', ') : 'none recorded'}.</p>
      <p data-testid="ppds-may-contain">Declared may contain: {label.may_contain?.length ? label.may_contain.map(allergenName).join(', ') : 'none recorded'}.</p>

      <section data-testid="ppds-recipe-level">
        <h3>Recipe-level declarations</h3>
        {(label.recipe_level_declarations || []).length
          ? <ul>{(label.recipe_level_declarations || []).map(decl => <li key={decl.ref_id} data-testid="ppds-recipe-declaration">
            {decl.is_root ? 'This recipe' : `Sub-recipe ${decl.name || decl.ref_id}`}
            {(decl.contains || []).length ? ` — declared contains ${decl.contains.map(allergenName).join(', ')}` : ''}
            {(decl.may_contain || []).length ? ` — declared may contain ${decl.may_contain.map(allergenName).join(', ')}` : ''}
            . These are declared against the recipe, not against a listed ingredient.
          </li>)}</ul>
          : <p>No recipe-level allergen declarations are recorded for this recipe.</p>}
      </section>

      {(label.issues || []).length > 0 && <section data-testid="ppds-issues">
        <h3>Unverified inputs</h3>
        <p>Each entry below is a reason this label cannot be treated as complete.</p>
        <ul>{(label.issues || []).map((issue, index) => <li key={index}>{issue.reason} ({issue.kind} {issue.ref_id})</li>)}</ul>
      </section>}
      {(label.advisories || []).length > 0 && <section data-testid="ppds-advisories">
        <h3>Advisories</h3>
        <ul>{(label.advisories || []).map((advisory, index) => <li key={index}>{advisory.reason} ({advisory.kind} {advisory.ref_id})</li>)}</ul>
      </section>}

      <section data-testid="ppds-limitations">
        <h3>Limitations</h3>
        <ul>{(label.limitations || []).map((line, index) => <li key={index}>{line}</li>)}</ul>
      </section>
      <p className="wf-notice" data-testid="ppds-notice">{label.notice}</p>

      <PrintPanel label={label} confirmed={confirmed} setConfirmed={setConfirmed} />
    </section>}
  </main>;
}

function PrintPanel({ label, confirmed, setConfirmed }) {
  const blockers = printBlockers(label);
  if (blockers.length) {
    return <section data-testid="ppds-print-blocked">
      <h3>Printing unavailable</h3>
      <ul>{blockers.map(reason => <li key={reason}>{reason}</li>)}</ul>
    </section>;
  }
  const dateWord = DATE_WORDS[label.date_marking.type] || 'Date marking';
  const mayContain = (label.may_contain || []).map(allergenName);
  return <section data-testid="ppds-print-panel">
    <h3>Print</h3>
    <label>
      <input type="checkbox" data-testid="ppds-print-confirm" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
      {' '}I have checked the name, ingredients, allergens and date marking against the product and supplier statements.
    </label>
    <div className="wf-actions">
      <button type="button" data-testid="ppds-print" disabled={!confirmed} onClick={() => window.print()}>Print label</button>
    </div>
    <div className="ppds-print-area" data-testid="ppds-print-area" aria-hidden="true">
      <p className="ppds-print-name">{label.name_of_food}</p>
      <p><strong>Ingredients:</strong> <EmphasisedText text={label.ingredients_text} /></p>
      {mayContain.length > 0 && <p>May contain: {mayContain.join(', ')}.</p>}
      <p>{dateWord}: {label.date_marking.value}</p>
    </div>
  </section>;
}
